import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import apiService from '../services/api';
import AppInstall from './AppInstall';
import './Auth.css';

const Register = () => {
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: Credentials, 2: OTP Verification
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    otp: '',
    countryCode: '',
    currency: '',
    referralCode: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [activeHint, setActiveHint] = useState(null);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({
    hasLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false
  });
  const [otpSent, setOtpSent] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [canResendOtp, setCanResendOtp] = useState(true);
  
  // New state for countries and currencies
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [referralPreview, setReferralPreview] = useState('');
  const [referralLocked, setReferralLocked] = useState(false);

  // Look up a referral code → masked referrer name (public endpoint)
  const handleReferralLookup = async (code) => {
    if (!code) { setReferralPreview(''); return; }
    const base = process.env.REACT_APP_API_URL || window.location.origin;
    try {
      const res = await fetch(`${base}/api/promotions/lookup-referral?code=${encodeURIComponent(code)}`);
      const json = await res.json();
      setReferralPreview(json.success ? json.maskedName : '');
    } catch (e) { setReferralPreview(''); }
  };

  useEffect(() => {
    const code = formData.referralCode;
    if (!code) { setReferralPreview(''); return; }
    const t = setTimeout(() => handleReferralLookup(code), 400);
    return () => clearTimeout(t);
  }, [formData.referralCode]);

  // Read referral code from URL (?ref=<code>) — auto-fill and lock the field.
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setFormData(prev => ({ ...prev, referralCode: ref.trim() }));
      setReferralLocked(true);
    }
  }, [searchParams]);

  useEffect(() => {
    // Clear errors when form data changes
    if (Object.keys(errors).length > 0) {
      setErrors({});
    }
  }, [formData]);

  // OTP countdown timer
  useEffect(() => {
    let timer;
    if (otpCountdown > 0) {
      timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
    } else if (otpCountdown === 0 && !canResendOtp) {
      setCanResendOtp(true);
    }
    return () => clearTimeout(timer);
  }, [otpCountdown, canResendOtp]);

  // Load countries on component mount
  useEffect(() => {
    fetchActiveCountries();
  }, []);

  // Check username availability
  useEffect(() => {
    const checkUsernameAvailability = async () => {
      if (formData.username.length >= 3) {
        try {
          const response = await apiService.post('/api/auth/check-username', {
            username: formData.username
          });
          
          if (response.success) {
            setUsernameAvailable(response.available);
            
            if (!response.available) {
              setErrors(prev => ({
                ...prev,
                username: 'Username not available. Try adding numbers.'
              }));
            } else {
              setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.username;
                return newErrors;
              });
            }
          }
        } catch (error) {
          console.error('Error checking username:', error);
        }
      }
    };

    if (formData.username) {
      checkUsernameAvailability();
    } else {
      setUsernameAvailable(null);
    }
  }, [formData.username]);

  // Check password strength
  useEffect(() => {
    const password = formData.password;
    setPasswordStrength({
      hasLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[@$!%*?&]/.test(password)
    });
  }, [formData.password]);

  // Fetch active countries from backend
  const fetchActiveCountries = async () => {
    setLoadingCountries(true);
    try {
      const response = await apiService.get('/api/auth/countries/active');
      if (response.success) {
        setCountries(response.countries);
        
        // Auto-select first country and its default currency
        if (response.countries.length > 0) {
          const firstCountry = response.countries[0];
          const defaultCurrency = firstCountry.currency_code || 'USD';
          
          setFormData(prev => ({
            ...prev,
            countryCode: firstCountry.country_code,
            currency: defaultCurrency
          }));
          
          // Set currencies for the first country
          setCurrencies([{
            currency_code: defaultCurrency,
            currency_symbol: firstCountry.currency_symbol || '$'
          }]);
        }
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      // Set some default countries for development
      const defaultCountries = [
        { 
          id: 1, 
          country_code: 'US', 
          country_name: 'United States', 
          currency_code: 'USD', 
          currency_symbol: '$',
          is_active: true 
        },
        { 
          id: 2, 
          country_code: 'IN', 
          country_name: 'India', 
          currency_code: 'INR', 
          currency_symbol: '₹',
          is_active: true 
        },
        { 
          id: 3, 
          country_code: 'LR', 
          country_name: 'Liberia', 
          currency_code: 'LRD', 
          currency_symbol: 'L$',
          is_active: true 
        },
        { 
          id: 4, 
          country_code: 'BJ', 
          country_name: 'Benin', 
          currency_code: 'CFA', 
          currency_symbol: 'CFA',
          is_active: true 
        }
      ];
      
      setCountries(defaultCountries);
      
      if (defaultCountries.length > 0) {
        const firstCountry = defaultCountries[0];
        setFormData(prev => ({
          ...prev,
          countryCode: firstCountry.country_code,
          currency: firstCountry.currency_code
        }));
        setCurrencies([{
          currency_code: firstCountry.currency_code,
          currency_symbol: firstCountry.currency_symbol
        }]);
      }
      
      setErrors({ general: 'Failed to load countries from server. Using defaults.' });
    } finally {
      setLoadingCountries(false);
    }
  };

  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    const selectedCountry = countries.find(c => c.country_code === countryCode);
    
    if (selectedCountry) {
      const defaultCurrency = selectedCountry.currency_code || 'USD';
      const currencySymbol = selectedCountry.currency_symbol || '$';
      
      setFormData(prev => ({
        ...prev,
        countryCode,
        currency: defaultCurrency
      }));
      
      setCurrencies([{
        currency_code: defaultCurrency,
        currency_symbol: currencySymbol
      }]);
    }
  };

  const handleCurrencyChange = (e) => {
    setFormData(prev => ({
      ...prev,
      currency: e.target.value
    }));
  };

  // ADD THIS MISSING FUNCTION
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFocus = (fieldName) => {
    setActiveHint(fieldName);
  };

  const handleBlur = () => {
    // Keep hint visible for a moment after blur for better UX
    setTimeout(() => {
      setActiveHint(null);
    }, 200);
  };

  const validateCredentials = () => {
    const newErrors = {};

    // Username validation
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    } else if (formData.username.length < 3 || formData.username.length > 20) {
      newErrors.username = 'Username must be between 3 and 20 characters';
    } else if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      newErrors.username = 'Username can only contain letters, numbers, and underscores';
    } else if (usernameAvailable === false) {
      newErrors.username = 'Username is already taken';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!passwordStrength.hasLength) {
      newErrors.password = 'Password must be at least 8 characters long';
    } else if (!passwordStrength.hasUppercase) {
      newErrors.password = 'Password must contain at least one uppercase letter';
    } else if (!passwordStrength.hasLowercase) {
      newErrors.password = 'Password must contain at least one lowercase letter';
    } else if (!passwordStrength.hasNumber) {
      newErrors.password = 'Password must contain at least one number';
    } else if (!passwordStrength.hasSpecial) {
      newErrors.password = 'Password must contain at least one special character (@, $, !, %, *, ?, &)';
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Country validation
    if (!formData.countryCode) {
      newErrors.countryCode = 'Please select your country';
    }

    // Currency validation
    if (!formData.currency) {
      newErrors.currency = 'Please select your currency';
    }

    return newErrors;
  };

  const validateOTP = () => {
    const newErrors = {};

    if (!formData.otp.trim()) {
      newErrors.otp = 'OTP is required';
    } else if (!/^\d{6}$/.test(formData.otp)) {
      newErrors.otp = 'OTP must be 6 digits';
    }

    return newErrors;
  };

  const handleSendOTP = async () => {
    const formErrors = validateCredentials();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    
    try {
      console.log('📤 Sending registration request...');
      
      const response = await apiService.post('/api/auth/register/initiate', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        countryCode: formData.countryCode,
        currency: formData.currency,
        referralCode: formData.referralCode
      });

      console.log('✅ Registration response:', response);

      if (response.success) {
        setOtpSent(true);
        setStep(2);
        setOtpCountdown(60); // 60 seconds countdown
        setCanResendOtp(false);
        
        const countryName = countries.find(c => c.country_code === formData.countryCode)?.country_name || formData.countryCode;
        setSuccessMessage(`✅ OTP sent to ${formData.email}!`);
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        throw new Error(response.error || 'Failed to send OTP');
      }
      
    } catch (error) {
      console.error('❌ Send OTP error:', error);
      
      let errorMessage = error.message || 'Failed to send OTP. Please try again.';
      
      if (error.message.includes('Email is already registered')) {
        errorMessage = 'This email is already registered. If you forgot your password, click "Forgot Password" on the sign-in page.';
      } else if (error.message.includes('Username is already taken')) {
        errorMessage = 'Username is already taken. Please choose another.';
      }
      
      setErrors({ general: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResendOtp) return;

    setIsSubmitting(true);
    setErrors({});
    
    try {
      const response = await apiService.post('/api/auth/register/resend-otp', {
        email: formData.email
      });

      if (response.success) {
        setOtpCountdown(60);
        setCanResendOtp(false);
        setSuccessMessage('📬 New OTP sent to your email!');
        
        setTimeout(() => {
          setSuccessMessage('');
        }, 5000);
      } else {
        throw new Error(response.error || 'Failed to resend OTP');
      }
      
    } catch (error) {
      console.error('❌ Resend OTP error:', error);
      setErrors({ general: error.message || 'Failed to resend OTP. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    const formErrors = validateOTP();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    
    try {
      console.log('🔐 Verifying OTP and creating account...');
      
      const response = await apiService.post('/api/auth/register/verify', {
        email: formData.email,
        otp: formData.otp
      });

      console.log('✅ Account creation response:', response);

      if (response.success) {
        setSuccessMessage('🎉 Account created successfully! Redirecting to login...');
        
        // Show user details
        console.log('📝 User created with:', {
          username: response.user.username,
          email: response.user.email,
          country: response.user.country,
          currency: response.user.currency,
          wallets: response.user.wallets
        });
        
        // Clear form
        setFormData({
          username: '',
          email: '',
          password: '',
          confirmPassword: '',
          otp: '',
          countryCode: '',
          currency: ''
        });

        // Redirect to login after 3 seconds
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      } else {
        throw new Error(response.error || 'Account creation failed');
      }
      
    } catch (error) {
      console.error('❌ OTP verification error:', error);
      
      if (error.message.includes('Invalid OTP')) {
        setErrors({ otp: 'Invalid OTP. Please try again.' });
      } else if (error.message.includes('OTP expired')) {
        setErrors({ otp: 'OTP has expired. Please request a new one.' });
      } else {
        setErrors({ general: error.message || 'Account creation failed. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep(1);
    setOtpSent(false);
    setErrors({});
    setSuccessMessage('');
  };

  // Generate suggested usernames
  const getSuggestedUsernames = () => {
    const base = formData.username.toLowerCase().replace(/[^a-z0-9]/g, '');
    const suggestions = [];
    
    for (let i = 1; i <= 3; i++) {
      suggestions.push(`${base}${Math.floor(Math.random() * 1000)}`);
    }
    
    return suggestions;
  };

  // Hint content based on active field and step
  const getHintContent = () => {
    if (step === 2) {
      return {
        title: 'Email Verification',
        content: (
          <div>
            <p style={{marginBottom: '10px'}}>We sent a 6-digit OTP to:</p>
            <p style={{fontWeight: 'bold', color: '#1a5319', marginBottom: '15px'}}>
              {formData.email}
            </p>
            
            {process.env.NODE_ENV === 'development' && (
              <div style={{ 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffeaa7',
                borderRadius: '5px',
                padding: '10px',
                marginBottom: '15px'
              }}>
                <p style={{ fontWeight: 'bold', color: '#856404', marginBottom: '5px' }}>
                  🛠️ Development Mode
                </p>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404' }}>
                  Use OTP: <strong>123456</strong>
                </p>
              </div>
            )}
            
            {formData.countryCode && (
              <div style={{marginBottom: '10px', padding: '8px', background: '#f0f9ff', borderRadius: '5px'}}>
                <p style={{fontSize: '0.85rem', margin: 0}}>
                  <strong>Country:</strong> {countries.find(c => c.country_code === formData.countryCode)?.country_name || formData.countryCode}
                </p>
                <p style={{fontSize: '0.85rem', margin: '5px 0 0 0'}}>
                  <strong>Currency:</strong> {formData.currency}
                </p>
              </div>
            )}
            
            <div style={{marginBottom: '15px'}}>
              <p style={{fontSize: '0.85rem', marginBottom: '8px'}}>Didn't receive the OTP?</p>
              {canResendOtp ? (
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={isSubmitting}
                  style={{
                    background: 'transparent',
                    border: '2px solid #28a745',
                    borderRadius: '5px',
                    padding: '8px 15px',
                    fontSize: '0.8rem',
                    color: '#28a745',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  Resend OTP
                </button>
              ) : (
                <p style={{color: '#666', fontSize: '0.8rem'}}>
                  Resend available in {otpCountdown} seconds
                </p>
              )}
            </div>

            <div style={{borderTop: '1px solid #ddd', paddingTop: '10px'}}>
              <p style={{fontSize: '0.8rem', color: '#666', marginBottom: '5px'}}>
                Make sure to check your spam folder if you don't see the email.
              </p>
            </div>
          </div>
        )
      };
    }

    switch (activeHint) {
      case 'username':
        return {
          title: 'Username Format',
          content: (
            <div>
              <p style={{marginBottom: '10px'}}>Format: <strong>Vijo1234</strong></p>
              
              {formData.username && (
                <div>
                  {usernameAvailable === false && (
                    <div style={{marginBottom: '15px'}}>
                      <p style={{color: '#dc3545', fontWeight: 'bold', marginBottom: '8px'}}>
                        Username not available
                      </p>
                      <p style={{fontSize: '0.85rem', marginBottom: '8px'}}>Suggestions:</p>
                      <div style={{display: 'flex', flexDirection: 'column', gap: '5px'}}>
                        {getSuggestedUsernames().map((suggestion, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, username: suggestion }))}
                            style={{
                              background: 'transparent',
                              border: '1px solid #28a745',
                              borderRadius: '5px',
                              padding: '5px 10px',
                              fontSize: '0.8rem',
                              color: '#28a745',
                              cursor: 'pointer',
                              textAlign: 'left'
                            }}
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {usernameAvailable === true && (
                    <p style={{color: '#28a745', fontWeight: 'bold'}}>
                      ✓ Username available
                    </p>
                  )}
                </div>
              )}
              
              {!formData.username && (
                <div>
                  <p style={{marginBottom: '8px'}}>Requirements:</p>
                  <ul className="hint-list">
                    <li>3-20 characters</li>
                    <li>Letters, numbers, underscores only</li>
                    <li>No spaces or special characters</li>
                  </ul>
                </div>
              )}
            </div>
          )
        };
      case 'email':
        return {
          title: 'Email Format',
          content: (
            <div>
              <p style={{marginBottom: '10px'}}>Example: <strong>vijo1234@gmail.com</strong></p>
              
              {formData.email && !/\S+@\S+\.\S+/.test(formData.email) && (
                <p style={{color: '#dc3545', fontSize: '0.85rem', marginTop: '8px'}}>
                  Please enter a valid email address
                </p>
              )}
              
              {formData.email && /\S+@\S+\.\S+/.test(formData.email) && (
                <p style={{color: '#28a745', fontWeight: 'bold', marginTop: '8px'}}>
                  ✓ Valid email format
                </p>
              )}
            </div>
          )
        };
      case 'password':
        return {
          title: 'Password Requirements',
          content: (
            <div>
              <div style={{marginBottom: '15px'}}>
                {formData.password && (
                  <div style={{display: 'flex', flexDirection: 'column', gap: '5px'}}>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <span style={{color: passwordStrength.hasLength ? '#28a745' : '#dc3545'}}>
                        {passwordStrength.hasLength ? '✓' : '✗'}
                      </span>
                      <span style={{fontSize: '0.85rem'}}>At least 8 characters</span>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <span style={{color: passwordStrength.hasUppercase ? '#28a745' : '#dc3545'}}>
                        {passwordStrength.hasUppercase ? '✓' : '✗'}
                      </span>
                      <span style={{fontSize: '0.85rem'}}>One uppercase letter (A-Z)</span>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <span style={{color: passwordStrength.hasLowercase ? '#28a745' : '#dc3545'}}>
                        {passwordStrength.hasLowercase ? '✓' : '✗'}
                      </span>
                      <span style={{fontSize: '0.85rem'}}>One lowercase letter (a-z)</span>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <span style={{color: passwordStrength.hasNumber ? '#28a745' : '#dc3545'}}>
                        {passwordStrength.hasNumber ? '✓' : '✗'}
                      </span>
                      <span style={{fontSize: '0.85rem'}}>One number (0-9)</span>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                      <span style={{color: passwordStrength.hasSpecial ? '#28a745' : '#dc3545'}}>
                        {passwordStrength.hasSpecial ? '✓' : '✗'}
                      </span>
                      <span style={{fontSize: '0.85rem'}}>One special character (@$!%*?&)</span>
                    </div>
                  </div>
                )}
              </div>
              
              {!formData.password && (
                <div>
                  <p style={{marginBottom: '8px'}}>Your password must include:</p>
                  <ul className="hint-list">
                    <li>8+ characters</li>
                    <li>Uppercase & lowercase letters</li>
                    <li>Numbers</li>
                    <li>Special characters (@$!%*?&)</li>
                  </ul>
                </div>
              )}
            </div>
          )
        };
      case 'confirmPassword':
        return {
          title: 'Confirm Password',
          content: (
            <div>
              <p style={{marginBottom: '10px'}}>Re-enter your password to confirm</p>
              
              {formData.confirmPassword && (
                <div>
                  {formData.password === formData.confirmPassword ? (
                    <p style={{color: '#28a745', fontWeight: 'bold'}}>
                      ✓ Passwords match
                    </p>
                  ) : (
                    <p style={{color: '#dc3545', fontWeight: 'bold'}}>
                      ✗ Passwords do not match
                    </p>
                  )}
                </div>
              )}
            </div>
          )
        };
      case 'countryCode':
        return {
          title: 'Country Selection',
          content: (
            <div>
              <p style={{marginBottom: '10px'}}>Select your country to determine your currency</p>
              
              {formData.countryCode && (
                <div>
                  <p style={{color: '#28a745', fontWeight: 'bold'}}>
                    ✓ Country selected
                  </p>
                  <p style={{fontSize: '0.85rem', marginTop: '5px'}}>
                    Currency will be set automatically
                  </p>
                </div>
              )}
              
              {!formData.countryCode && (
                <div>
                  <p style={{marginBottom: '8px'}}>Why we need your country:</p>
                  <ul className="hint-list">
                    <li>Determines your local currency</li>
                    <li>Sets up your wallet correctly</li>
                    <li>Shows country-specific games</li>
                  </ul>
                </div>
              )}
            </div>
          )
        };
      case 'currency':
        return {
          title: 'Currency Selection',
          content: (
            <div>
              <p style={{marginBottom: '10px'}}>Your wallet currency</p>
              
              {formData.currency && (
                <div>
                  <p style={{color: '#28a745', fontWeight: 'bold'}}>
                    ✓ Currency: {formData.currency}
                  </p>
                  <p style={{fontSize: '0.85rem', marginTop: '5px'}}>
                    {formData.currency === 'USD' && 'US Dollar - Global currency'}
                    {formData.currency === 'INR' && 'Indian Rupee - For Indian users'}
                    {formData.currency === 'LRD' && 'Liberian Dollar - For Liberian users'}
                    {formData.currency === 'CFA' && 'West African CFA franc - For Benin users'}
                  </p>
                </div>
              )}
              
              {!formData.currency && (
                <div>
                  <p style={{marginBottom: '8px'}}>Available currencies:</p>
                  <ul className="hint-list">
                    <li>USD - US Dollar</li>
                    <li>INR - Indian Rupee</li>
                    <li>LRD - Liberian Dollar</li>
                    <li>CFA - West African CFA</li>
                  </ul>
                </div>
              )}
            </div>
          )
        };
      default:
        return {
          title: step === 1 ? 'Registration Help' : 'Email Verification',
          content: step === 1 
            ? <p>Click on any field to see specific requirements and examples.</p>
            : <p>Enter the 6-digit OTP sent to your email address to verify your account.</p>
        };
    }
  };

  const hint = getHintContent();

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <div className="logo">
            <span className="logo-icon">🎴</span>
            <h1>JV Rummy</h1>
          </div>
          <p>
            {step === 1 ? 'Create your account to start playing' : 'Verify your email address'}
          </p>
        </div>

        <div className="auth-form-container">
          {successMessage && (
            <div className="success-message">
              {successMessage}
            </div>
          )}

          {errors.general && (
            <div className="error-message">
              {errors.general}
            </div>
          )}

          <form onSubmit={step === 1 ? (e) => { e.preventDefault(); handleSendOTP(); } : handleVerifyOTP} className="form-layout">
            <div className="form-column">
              {step === 1 ? (
                // Step 1: Credentials Form
                <>
                  <div className="form-group">
                    <label>Username</label>
                    <input
                      type="text"
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      onFocus={() => handleFocus('username')}
                      onBlur={handleBlur}
                      className={errors.username ? 'error' : ''}
                      placeholder="Enter your username"
                      disabled={isSubmitting || loadingCountries}
                    />
                    {errors.username && <span className="error-text">{errors.username}</span>}
                  </div>

                  <div className="form-group">
                    <label>Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onFocus={() => handleFocus('email')}
                      onBlur={handleBlur}
                      className={errors.email ? 'error' : ''}
                      placeholder="Enter your email"
                      disabled={isSubmitting || loadingCountries}
                    />
                    {errors.email && <span className="error-text">{errors.email}</span>}
                  </div>

                  <div className="form-group">
                    <label>Password</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      onFocus={() => handleFocus('password')}
                      onBlur={handleBlur}
                      className={errors.password ? 'error' : ''}
                      placeholder="Create a strong password"
                      disabled={isSubmitting || loadingCountries}
                    />
                    {errors.password && <span className="error-text">{errors.password}</span>}
                  </div>

                  <div className="form-group">
                    <label>Confirm Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      onFocus={() => handleFocus('confirmPassword')}
                      onBlur={handleBlur}
                      className={errors.confirmPassword ? 'error' : ''}
                      placeholder="Confirm your password"
                      disabled={isSubmitting || loadingCountries}
                    />
                    {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                  </div>

                  <div className="form-group">
                    <label>Country</label>
                    <select
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleCountryChange}
                      onFocus={() => handleFocus('countryCode')}
                      onBlur={handleBlur}
                      className={errors.countryCode ? 'error' : ''}
                      disabled={isSubmitting || loadingCountries}
                    >
                      <option value="">Select your country</option>
                      {loadingCountries ? (
                        <option value="">Loading countries...</option>
                      ) : (
                        countries.map(country => (
                          <option key={country.country_code} value={country.country_code}>
                            {country.country_name} ({country.currency_code})
                          </option>
                        ))
                      )}
                    </select>
                    {errors.countryCode && <span className="error-text">{errors.countryCode}</span>}
                  </div>

                  <div className="form-group">
                    <label>Referral Code {referralLocked ? '(from invite link)' : '(optional)'}</label>
                    <input
                      type="text"
                      name="referralCode"
                      value={formData.referralCode}
                      onChange={handleChange}
                      onFocus={() => handleFocus('referralCode')}
                      onBlur={handleBlur}
                      placeholder={referralLocked ? 'Applied from invite link' : 'e.g. 7'}
                      disabled={isSubmitting || loadingCountries}
                      readOnly={referralLocked}
                      style={referralLocked ? { background: '#f1f5f9', color: '#475569', cursor: 'not-allowed' } : undefined}
                    />
                    {referralPreview && (
                      <span style={{ display: 'block', fontSize: '0.8rem', color: '#28a745', marginTop: '4px', fontWeight: 'bold' }}>
                        Referred by: {referralPreview}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Currency</label>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '10px' 
                    }}>
                      <input
                        type="text"
                        name="currency"
                        value={formData.currency}
                        readOnly
                        className="readonly-field"
                        style={{ flex: 1 }}
                        onFocus={() => handleFocus('currency')}
                        onBlur={handleBlur}
                      />
                      <span style={{ 
                        fontSize: '1.2rem', 
                        fontWeight: 'bold',
                        color: '#2d3748',
                        minWidth: '40px'
                      }}>
                        {countries.find(c => c.country_code === formData.countryCode)?.currency_symbol || '$'}
                      </span>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '5px' }}>
                      Currency is automatically set based on your country
                      {formData.countryCode && (
                        <span style={{ marginLeft: '10px', fontWeight: 'bold', color: '#2d3748' }}>
                          {(() => {
                            const country = countries.find(c => c.country_code === formData.countryCode);
                            if (!country) return '';
                            if (country.country_code === 'BJ') return '(CFA Franc)';
                            if (country.country_code === 'LR') return '(Liberian Dollar)';
                            if (country.country_code === 'IN') return '(Indian Rupee)';
                            if (country.country_code === 'US') return '(US Dollar)';
                            return '(Local Currency)';
                          })()}
                        </span>
                      )}
                    </p>
                  </div>

                  <button 
                    type="submit" 
                    disabled={isSubmitting || loadingCountries}
                    className="auth-button"
                  >
                    {isSubmitting ? 'Sending OTP...' : (loadingCountries ? 'Loading countries...' : 'Send Verification OTP')}
                  </button>
                </>
              ) : (
                // Step 2: OTP Verification Form
                <>
                  <div className="form-group">
                    <label>Verification Code</label>
                    <input
                      type="text"
                      name="otp"
                      value={formData.otp}
                      onChange={handleChange}
                      onFocus={() => handleFocus('otp')}
                      onBlur={handleBlur}
                      className={errors.otp ? 'error' : ''}
                      placeholder="Enter 6-digit OTP"
                      disabled={isSubmitting}
                      maxLength="6"
                    />
                    {errors.otp && <span className="error-text">{errors.otp}</span>}
                    
                    {process.env.NODE_ENV === 'development' && (
                      <div style={{ marginTop: '5px' }}>
                        <p style={{ fontSize: '0.85rem', color: '#666' }}>
                          Development OTP: <strong>123456</strong>
                        </p>
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginTop: '0.5rem' }}>
                    <button 
                      type="button"
                      onClick={handleBackToCredentials}
                      disabled={isSubmitting}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        background: 'transparent',
                        color: '#666',
                        border: '2px solid #666',
                        borderRadius: '8px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      Back
                    </button>
                    <button 
                      type="submit" 
                      disabled={isSubmitting}
                      className="auth-button"
                      style={{ flex: 2 }}
                    >
                      {isSubmitting ? 'Verifying...' : 'Verify & Create Account'}
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="hints-column">
              <div className="hint-content">
                <div className="hint-title">{hint.title}</div>
                <div className="hint-text">{hint.content}</div>
              </div>
            </div>
          </form>
        </div>

        <div className="auth-footer">
          <p>Already have an account?</p>
          <Link to="/login" className="auth-link">
            Sign In
          </Link>
        </div>

        <AppInstall />
      </div>
    </div>
  );
};

export default Register;